/**
 * Review harness for `BoardTransition.tsx` — `/dev/board-transition` in `App.tsx`.
 *
 * Why this exists: the real mount point (`BoardTransitionGate.tsx`, wired into `PlaySurface.tsx`) can't be
 * exercised end to end today — `momentum-board-v2` sets `launchesChildren: false` and there is no shipped
 * mini-game module to launch as its child, so `view.child` never becomes truthy for the immersive world in
 * this build. This route drives the same `BoardTransition` component entirely from local props/state instead,
 * with "Under Pressure" as clearly-labelled illustrative example data (never hardcoded into the component
 * itself — see `BoardTransition.tsx`'s own note on that).
 *
 * Two ways to look at it:
 *  - Jump-to-stage buttons force a specific stage for straight side-by-side screenshot comparison against
 *    docs/concept/board/06-minimal-transition.png.
 *  - "Live" mode runs the real `useBoardTransitionStage` machine: press Ready as yourself, mark simulated
 *    teammates ready one at a time, and watch the countdown start itself the instant everyone is — there is
 *    no separate Start button anywhere in this screen.
 */
import { useState } from 'react';
import type { CharacterSlotId } from '@momentum/shared';
import { BoardTransition, type BoardTransitionUiStage } from './BoardTransition.tsx';
import { useBoardTransitionStage } from './useBoardTransitionStage.ts';
import { strings } from '../shell/strings.ts';
import { Link } from '../shell/router.tsx';

const DEMO_BRIEFING = {
  goal: 'Resolve the incident before time runs out.',
  play: 'Compare your clues. Agree on a move.',
  score: 'One point per resolved task.',
};

interface DemoPlayer {
  id: string;
  name: string;
  teamId: string;
  characterSlot: CharacterSlotId;
}

const DEMO_PLAYERS: DemoPlayer[] = [
  { id: 'you', name: 'You', teamId: 'blue', characterSlot: 'blue-oval' },
  { id: 'maya', name: 'Maya', teamId: 'blue', characterSlot: 'lilac-square' },
  { id: 'eric', name: 'Eric', teamId: 'coral', characterSlot: 'coral-triangle' },
  { id: 'sam', name: 'Sam', teamId: 'coral', characterSlot: 'green-diamond' },
];

const DEMO_TEAMS = [
  { id: 'blue', label: 'Team Blue', accentColor: 'var(--char-blue)' },
  { id: 'coral', label: 'Team Coral', accentColor: 'var(--char-coral)' },
];

const STAGE_OPTIONS: { id: BoardTransitionUiStage; label: string }[] = [
  { id: 'reveal', label: '1 · Reveal' },
  { id: 'briefing', label: '2 · Briefing' },
  { id: 'ready', label: '3 · Ready' },
  { id: 'countdown', label: '4 · Countdown' },
];

/** Stands in for the blurred board — this route mounts no real board, so it approximates the palette instead
 * of ever showing a flat colour where the design calls for a softened scene. */
function DemoBackdrop() {
  return (
    <svg viewBox="0 0 400 800" preserveAspectRatio="xMidYMid slice" width="100%" height="100%" role="presentation" aria-hidden="true">
      <defs>
        <linearGradient id="demo-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bfe0f7" />
          <stop offset="55%" stopColor="#8fc7ea" />
          <stop offset="100%" stopColor="#3f8fc4" />
        </linearGradient>
      </defs>
      <rect width="400" height="800" fill="url(#demo-sky)" />
      <circle cx="90" cy="180" r="120" fill="#ffffff" opacity="0.35" />
      <circle cx="320" cy="420" r="160" fill="#e7f6ff" opacity="0.3" />
      <circle cx="150" cy="620" r="140" fill="#2c6e9e" opacity="0.35" />
      <rect x="0" y="560" width="400" height="240" fill="#2f6f46" opacity="0.55" />
    </svg>
  );
}

export function BoardTransitionDemo() {
  const [forcedStage, setForcedStage] = useState<BoardTransitionUiStage | null>('reveal');
  const [youReady, setYouReady] = useState(false);
  const [simReady, setSimReady] = useState<Record<string, boolean>>({ maya: false, eric: false, sam: false });
  const [samDisconnected, setSamDisconnected] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const readyIds = new Set<string>([...(youReady ? ['you'] : []), ...Object.entries(simReady).filter(([, r]) => r).map(([id]) => id)]);

  const { stage: liveStage, countdownValue } = useBoardTransitionStage({
    totalPlayers: DEMO_PLAYERS.length,
    readyCount: readyIds.size,
    selfReady: youReady,
  });

  const stage = forcedStage ?? (liveStage === 'starting' ? 'countdown' : liveStage);
  const forcedCountdown = stage === 'countdown' && forcedStage !== null ? 3 : countdownValue;

  const players = DEMO_PLAYERS.map((p) => ({
    id: p.id,
    name: p.name,
    teamId: p.teamId,
    characterSlot: p.characterSlot,
    ready: forcedStage ? p.id !== 'sam' || !samDisconnected : readyIds.has(p.id),
    disconnected: p.id === 'sam' && samDisconnected,
  }));

  function reset() {
    setForcedStage('reveal');
    setYouReady(false);
    setSimReady({ maya: false, eric: false, sam: false });
    setSamDisconnected(false);
  }

  return (
    <div className="page stack">
      <h1>Board transition — review harness</h1>
      <p className="text-muted">
        Drives <code>BoardTransition.tsx</code> from local props only. Not part of the product — see the file
        header for why this route exists. <Link to="/">Home</Link>
      </p>

      <section className="card stack-sm" aria-label="Screenshot controls">
        <h2>Jump to stage (for screenshots)</h2>
        <div className="row">
          {STAGE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={forcedStage === option.id ? 'button card-selected' : 'button'}
              aria-pressed={forcedStage === option.id}
              onClick={() => setForcedStage(option.id)}
            >
              {option.label}
            </button>
          ))}
          <button type="button" className={forcedStage === null ? 'button card-selected' : 'button'} aria-pressed={forcedStage === null} onClick={() => setForcedStage(null)}>
            Live mode
          </button>
        </div>
      </section>

      {forcedStage === null ? (
        <section className="card stack-sm" aria-label="Live mode controls">
          <h2>Live mode</h2>
          <p className="text-muted">Press the real Ready button in the card as yourself, then mark teammates ready below. The countdown starts itself once all four are ready — there is no Start button.</p>
          <div className="row">
            {(['maya', 'eric', 'sam'] as const).map((id) => (
              <button key={id} type="button" className="button" aria-pressed={simReady[id]} onClick={() => setSimReady((s) => ({ ...s, [id]: !s[id] }))}>
                {DEMO_PLAYERS.find((p) => p.id === id)!.name}: {simReady[id] ? 'ready' : 'mark ready'}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="card row" aria-label="Other controls">
        <button type="button" className="button" aria-pressed={samDisconnected} onClick={() => setSamDisconnected((v) => !v)}>
          {samDisconnected ? 'Reconnect Sam' : 'Disconnect Sam'}
        </button>
        <button type="button" className="button" aria-pressed={reducedMotion} onClick={() => setReducedMotion((v) => !v)}>
          Reduced motion: {reducedMotion ? 'on' : 'off'}
        </button>
        <button type="button" className="button" onClick={reset}>
          Reset
        </button>
      </section>

      <div style={{ position: 'relative', height: '100dvh', minHeight: 640, border: '1px solid var(--line)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
        <BoardTransition
          stage={stage}
          gameTitle="Under Pressure"
          format="2 vs 2 · About 2 minutes"
          players={players}
          teams={DEMO_TEAMS}
          briefing={DEMO_BRIEFING}
          selfParticipantId="you"
          countdownValue={forcedCountdown}
          onReady={() => setYouReady(true)}
          readyDisabled={youReady}
          disconnectNotice={samDisconnected ? strings.transition.disconnectNotice('Sam') : null}
          hud={{ title: 'Momentum', crystalsCollected: 1, crystalsTotal: 3, onOpenMap: () => window.alert('Map (demo only)') }}
          backdrop={<DemoBackdrop />}
          reducedMotion={reducedMotion}
        />
      </div>

      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}
